import { TripShipmentLocation } from "../../graphql/generated";

/**
 * Gets the effective list of shipment IDs for a trip location.
 * Prioritizes shipmentIds array, falls back to shipmentId for backward compatibility.
 *
 * @param location - The trip shipment location
 * @returns Array of shipment ID strings
 */
export function getEffectiveShipmentIds(
  location:
    | Pick<TripShipmentLocation, "shipmentIds" | "shipmentId">
    | null
    | undefined,
): string[] {
  if (!location) {
    return [];
  }

  // Prioritize shipmentIds array if populated
  if (location.shipmentIds && location.shipmentIds.length > 0) {
    return location.shipmentIds;
  }

  // Fall back to single shipmentId for backward compatibility
  if (location.shipmentId) {
    return [location.shipmentId];
  }

  return [];
}

/**
 * Checks if a location has multiple shipments
 *
 * @param location - The trip shipment location
 * @returns True if the location has more than one shipment
 */
export function hasMultipleShipments(
  location:
    | Pick<TripShipmentLocation, "shipmentIds" | "shipmentId">
    | null
    | undefined,
): boolean {
  return getEffectiveShipmentIds(location).length > 1;
}

/**
 * Gets shipment objects for a location from a trip's shipments array
 *
 * @param location - The trip shipment location
 * @param shipments - Array of shipment objects with _id field
 * @returns Array of shipments that match the location's shipment IDs
 */
export function getShipmentsForLocation<T extends { _id: string }>(
  location:
    | Pick<TripShipmentLocation, "shipmentIds" | "shipmentId">
    | null
    | undefined,
  shipments: T[],
): T[] {
  const shipmentIds = getEffectiveShipmentIds(location);
  return shipments.filter((s) => shipmentIds.includes(s._id));
}
